import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { BoxComponentRef } from '../../../base/box/Box';
import { getImageHandlerSSR } from './utility';
import { hexToRgba, SECTION_HEIGHT } from '../../Commons';
import { GlobalStyles } from '@zeus/shared';
import Video from '../Video';

interface SectionContainerProps {
  builderMode?: boolean;
  altText?: string;
  onLoad?: () => void;
  onError?: () => void;
  MediaMap?: Map<string, any>;
  style?: React.CSSProperties;
  sx?: Record<string, any>;
  children?: ReactNode;
  isDesktop?: boolean;
  globalStyles?: GlobalStyles;
  baseStyles?: Record<string, any>;
  componentConfig?: any;
  componentId?: string;
  [key: string]: any;
  applyHeight?: boolean;
  parallaxEnabled?: boolean;
  onBackgroundClick?: (dataKey: string) => void;
}

const SectionContainer: React.FC<SectionContainerProps> = (props) => {
  const {
    builderMode = false,
    MediaMap,
    style = {},
    sx = {},
    children,
    onLoad,
    onError,
    altText,
    isDesktop = true,
    globalStyles,
    baseStyles = {},
    componentConfig,
    componentId,
    applyHeight = true,
    parallaxEnabled = false,
    onBackgroundClick,
    ...rest
  } = props;

  // Removed excessive logging - componentType is not in componentConfig
  const { sectionHeight, customHeight } = componentConfig || {};
  const [imageLoaded, setImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [parallaxY, setParallaxY] = useState(0);
  const backgroundVideoRef = useRef<HTMLVideoElement | null>(null);

  const handleImageLoad = () => {
    setImageLoaded(true);
    onLoad?.();
  };

  const handleImageError = () => {
    onError?.();
  };

  const handleBackgroundImageClick = (e: React.MouseEvent<HTMLElement>) => {
    if (builderMode && onBackgroundClick) {
      e.preventDefault();
      e.stopPropagation();
      onBackgroundClick('background');
    }
  };

  const handleBackgroundOverlayClick = (e: React.MouseEvent<HTMLElement>) => {
    if (builderMode && onBackgroundClick) {
      const target = e.target as HTMLElement;
      // Only trigger if clicking directly on overlay, not on content elements
      if (target === e.currentTarget || target.classList.contains('background-overlay')) {
        e.preventDefault();
        e.stopPropagation();
        onBackgroundClick('background');
      }
    }
  };

  const handleBackgroundOverlayMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    // Ensure overlay captures clicks even when content is present
    if (builderMode && onBackgroundClick) {
      const target = e.target as HTMLElement;
      if (target === e.currentTarget || target.classList.contains('background-overlay')) {
        e.stopPropagation();
      }
    }
  };

  const handleSectionContentClick = (e: React.MouseEvent<HTMLElement>) => {
    // Allow clicks to pass through to background if clicking on empty space within content area
    if (builderMode && onBackgroundClick) {
      const target = e.target as HTMLElement;
      const currentTarget = e.currentTarget as HTMLElement;

      // If clicking directly on section-content div (empty space), trigger background click
      if (target === currentTarget || (target.classList.contains('section-content') && target.children.length === 0)) {
        // Check if it's truly empty space (not on any child elements)
        const isInteractiveElement = target.closest(
          'button, a, input, [role="button"], [data-key], .editable-content, [onclick]'
        );
        const isTextElement = target.closest(
          'p, h1, h2, h3, h4, h5, h6, span, div[class*="Text"], div[class*="Heading"], div[class*="Description"]'
        );

        if (!isInteractiveElement && !isTextElement) {
          e.preventDefault();
          e.stopPropagation();
          onBackgroundClick('background');
        }
      }
    }
  };

  // Get image source with proper responsive handling
  const backgroundMediaType = componentConfig?.backgroundImage?.mediaType || 'STILL_IMAGE';
  const backgroundVideo = componentConfig?.backgroundVideo;
  const isBackgroundEnabled = Boolean(componentConfig?.backgroundImage?.toggle);
  const isVideoBackground = isBackgroundEnabled && backgroundMediaType === 'VIDEO' && Boolean(backgroundVideo?.value);
  const isImageBackground =
    isBackgroundEnabled && backgroundMediaType !== 'VIDEO' && Boolean(componentConfig?.backgroundImage?.value);

  const imageUrl = isImageBackground ? componentConfig?.backgroundImage?.value || '' : '';
  const imageData = isImageBackground
    ? getImageHandlerSSR({
        builderMode,
        imageUrl,
        MediaMap,
      })
    : null;

  // Use the same logic as the old version for image source selection
  const imageSrc = builderMode || !MediaMap ? imageUrl : imageData?.currentSrc;
  const { srcset, sizes, fallbackUrl } = imageData || {};

  const hasBackgroundImage = Boolean(componentConfig?.backgroundImage?.toggle && imageSrc);

  const createComponentStyles = () => {
    return {
      ...baseStyles,
      ...((!componentConfig?.backgroundImage?.toggle || !hasBackgroundImage) &&
        !componentConfig?.background?.value && { backgroundColor: globalStyles?.backgroundColor }),
      ...((!componentConfig?.backgroundImage?.toggle || !hasBackgroundImage) &&
        componentConfig?.background?.value && { backgroundColor: componentConfig?.background?.value }),
    };
  };

  // Create section height styles
  const createSectionHeightStyles = () => {
    if (sectionHeight === SECTION_HEIGHT.CUSTOM && customHeight) {
      return {
        minHeight: `${customHeight}px`,
        // Ensure content can grow beyond minHeight if needed
        height: 'auto',
      };
    }

    // Default height behavior - auto-adjusting based on content
    return {
      minHeight: 'auto',
      height: 'auto',
    };
  };

  const componentStyles = createComponentStyles();
  const sectionHeightStyles = createSectionHeightStyles();
  // Detect iOS devices (including iPad on iOS 13+ that report as Mac with touch)
  const isIOS =
    typeof window !== 'undefined'
      ? /iP(hone|od|ad)/.test(navigator.platform) ||
        (navigator.userAgent.includes('Mac') && 'ontouchend' in (document as any))
      : false;

  const parallaxAllowed = Boolean(parallaxEnabled && isImageBackground);
  const isFixedParallax = Boolean(parallaxAllowed && hasBackgroundImage);

  const useCssFixedParallax = isFixedParallax && !isIOS;
  const useStickyParallax = isFixedParallax && isIOS;

  // Combine all styles - merge sx properly with other styles
  const combinedSx = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    ...componentStyles,
    ...sectionHeightStyles,
    ...sx, // sx should override other styles
    padding: 'unset',
    ...(useCssFixedParallax && {
      backgroundImage: imageSrc ? `url(${imageSrc})` : undefined,
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }),
  };

  // JS parallax not needed for CSS fixed or sticky modes
  useEffect(() => {
    if (useCssFixedParallax || useStickyParallax) return;

    if (!parallaxAllowed) return;

    let rafId: number | null = null;

    const update = () => {
      rafId = null;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrolled = window.scrollY || window.pageYOffset;
      const elementTop = rect.top + scrolled;
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      if (scrolled + windowHeight > elementTop && scrolled < elementTop + rect.height) {
        const speed =
          typeof componentConfig?.backgroundImage?.parallaxSpeed === 'number'
            ? componentConfig.backgroundImage.parallaxSpeed
            : 0.3;
        const parallax = (scrolled - elementTop + windowHeight) * speed;
        setParallaxY(parallax);
      }
    };

    const onScroll = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll as any);
      window.removeEventListener('resize', onScroll as any);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [useCssFixedParallax, useStickyParallax, parallaxAllowed, componentConfig?.backgroundImage?.parallaxSpeed]);

  const parallaxImageStyle =
    parallaxEnabled && !isFixedParallax
      ? {
          transform: `translate3d(0, ${parallaxY}px, 0)`,
          willChange: 'transform',
          transition: 'transform 0.1s linear',
          height: '140%',
        }
      : {};

  const handleContainerClick = (e: React.MouseEvent) => {
    if (builderMode && onBackgroundClick) {
      const target = e.target as HTMLElement;
      const currentTarget = e.currentTarget as HTMLElement;

      // Check if clicking on background elements (images, videos, or empty space)
      const isBackgroundImage = target.tagName === 'IMG' && target.getAttribute('alt') === 'Background';
      const isBackgroundVideo = target.tagName === 'VIDEO';
      const isOverlay = target.classList.contains('background-overlay');
      const isDirectClick = target === currentTarget;

      // Don't trigger if clicking on interactive elements or content
      const isInteractiveElement = target.closest(
        'button, a, input, [role="button"], [data-key], .editable-content, [onclick]'
      );
      const isContentElement = target.closest('.section-content > *');
      const isMenuItem = target.closest(
        '[class*="MenuItem"], [class*="menu-item"], img[alt*="item"], img[alt*="Item"]'
      );
      const isTextElement = target.closest(
        'p, h1, h2, h3, h4, h5, h6, span, div[class*="Text"], div[class*="Heading"], div[class*="Description"]'
      );

      // Check if clicking on empty space - container itself or empty section-content
      const isEmptySpace =
        isDirectClick ||
        target === currentTarget ||
        (target.classList.contains('section-content') && target.children.length === 0);

      // Allow background click if clicking on background image/video, overlay, or empty space
      // But exclude clicks on menu items, text elements, or interactive elements
      if (
        (isBackgroundImage || isBackgroundVideo || isOverlay || isEmptySpace) &&
        !isInteractiveElement &&
        !isContentElement &&
        !isMenuItem &&
        !isTextElement
      ) {
        e.preventDefault();
        e.stopPropagation();
        onBackgroundClick('background');
      }
    }
  };

  return (
    <BoxComponentRef
      ref={containerRef as any}
      style={style}
      sx={{ ...combinedSx, cursor: builderMode && onBackgroundClick ? 'pointer' : 'default' }}
      onClick={(e) => handleContainerClick(e)}
      {...rest}
    >
      {/* Sticky background for Apple devices (CSS-only) */}
      {isImageBackground && imageSrc && useStickyParallax && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
            }}
          >
            <div
              style={{
                position: 'sticky',
                top: 0,
                width: '100%',
                height: '100vh',
                backgroundImage: `url(${imageSrc})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </div>
          <div
            onClick={(e) => handleBackgroundOverlayClick(e)}
            className="background-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: hexToRgba(
                componentConfig?.backgroundImage?.overlayColor,
                componentConfig?.backgroundImage?.overlayOpacity
              ),
              zIndex: 1,
              pointerEvents: builderMode ? 'auto' : 'none',
              cursor: builderMode ? 'pointer' : 'default',
            }}
          />
        </>
      )}

      {/* Render background image if toggled on (only when not using CSS fixed or sticky modes) */}
      {isImageBackground && imageSrc && !useCssFixedParallax && !useStickyParallax && (
        <>
          {builderMode || !MediaMap ? (
            <img
              loading="lazy"
              src={imageSrc}
              alt={altText || 'Background'}
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={(e) => handleBackgroundImageClick(e)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: `blur(${componentConfig?.backgroundImage?.blurValue}px)`,
                zIndex: 0,
                cursor: builderMode ? 'pointer' : 'default',
                ...parallaxImageStyle,
              }}
            />
          ) : !imageLoaded ? (
            <img
              src={fallbackUrl || imageUrl}
              alt={altText || 'Background'}
              loading="lazy"
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={(e) => handleBackgroundImageClick(e)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: `blur(${componentConfig?.backgroundImage?.blurValue}px)`,
                zIndex: 0,
                cursor: builderMode ? 'pointer' : 'default',
                ...parallaxImageStyle,
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <img
              src={imageSrc}
              srcSet={srcset}
              sizes={sizes}
              alt={altText || 'Background'}
              loading="lazy"
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={(e) => handleBackgroundImageClick(e)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 0,
                cursor: builderMode ? 'pointer' : 'default',
                // Combine user-defined blur with loading blur
                filter: imageLoaded ? `blur(${componentConfig?.backgroundImage?.blurValue || 0}px)` : 'blur(20px)',
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out, filter 0.5s ease-in-out',
                ...parallaxImageStyle,
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
          {/* Overlay */}
          <div
            onClick={(e) => handleBackgroundOverlayClick(e)}
            onMouseDown={() => handleBackgroundOverlayMouseDown}
            className="background-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: hexToRgba(
                componentConfig?.backgroundImage?.overlayColor,
                componentConfig?.backgroundImage?.overlayOpacity
              ),
              zIndex: 1,
              pointerEvents: builderMode ? 'auto' : 'none',
              cursor: builderMode ? 'pointer' : 'default',
            }}
          />
        </>
      )}

      {/* Overlay for CSS fixed mode */}
      {isImageBackground && useCssFixedParallax && (
        <div
          onClick={handleBackgroundOverlayClick}
          className="background-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: hexToRgba(
              componentConfig?.backgroundImage?.overlayColor,
              componentConfig?.backgroundImage?.overlayOpacity
            ),
            zIndex: 1,
            pointerEvents: builderMode ? 'auto' : 'none',
            cursor: builderMode ? 'pointer' : 'default',
          }}
        />
      )}

      {isVideoBackground && backgroundVideo?.value && (
        <>
          <Video
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover' as const,
              zIndex: 0,
              cursor: builderMode ? 'pointer' : 'default',
              filter: `blur(${componentConfig?.backgroundImage?.blurValue}px)`,
            }}
            playsInline
            autoPlay
            muted
            lazyLoadPlaceholder
            loop
            hideControls
            videoRef={backgroundVideoRef}
            videoUrl={backgroundVideo.value}
            mediaMap={MediaMap}
            placeholderUrl={backgroundVideo.placeholder}
            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => handleBackgroundImageClick(e)}
          />
          <div
            onClick={(e) => handleBackgroundOverlayClick(e)}
            className="background-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: hexToRgba(
                componentConfig?.backgroundImage?.overlayColor,
                componentConfig?.backgroundImage?.overlayOpacity
              ),
              zIndex: 1,
              pointerEvents: builderMode ? 'auto' : 'none',
              cursor: builderMode ? 'pointer' : 'default',
            }}
          />
        </>
      )}

      {/* Content */}
      <div
        className="section-content"
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          minHeight: 'auto',
          padding: componentStyles.padding,
          pointerEvents: 'auto',
        }}
        onClick={(e) => handleSectionContentClick(e)}
      >
        {children}
      </div>
    </BoxComponentRef>
  );
};

export default SectionContainer;
